/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import {
    RequiredValidation,
    FlexCol,
    FlexRow,
    Heading,
    Stack,
    TextField,
    Text,
} from 'sarsaparilla';
import SmartSelector from '../../src/components/registration/SmartSelector';
import { FLEX_FIELD_1, FLEX_FIELD_2 } from '../constants/propPaths';

const defaultDataBindingMap = {
    [FLEX_FIELD_1]: 'flex_field_1',
    [FLEX_FIELD_2]: 'flex_field_2',
};

const TEXT_FIELD = 1;
const DROPDOWN_FIELD = 2;

export default function (dataBindingsMap = defaultDataBindingMap) {
    function FlexField({
        permit,
        reservation,
        pActions,
        validation,
        shouldValidateOnBEOnly,
        displayValidationError,
    }) {
        const dm = { ...defaultDataBindingMap, ...dataBindingsMap };

        const onFormValidate = (isInvalid, error, path) => {
            return pActions.formValidationChange(
                `${reservation?.id}_${path}`,
                isInvalid ? error : null
            );
        };

        const handleFieldTextChange = ({ target }) => {
            const { value: fieldText, name: FLEX_FIELD_TYPE } = target;
            pActions.updatePendingReservation(
                reservation.id,
                dm[FLEX_FIELD_TYPE],
                fieldText
            );
        };

        const renderField = (flexField, index) => {
            const fieldName = index === 0 ? FLEX_FIELD_1 : FLEX_FIELD_2;
            const fieldText = get(reservation, dm[fieldName], '');
            const isRequired = get(flexField, 'is_required', false);
            const hasValidationError =
                (shouldValidateOnBEOnly && get(validation, dm[fieldName])) ||
                displayValidationError;
            const validationError =
                !fieldText && isRequired && hasValidationError ? 'rec-error' : '';

            if (flexField.type === 1) {
                return (
                    <>
                        <TextField
                            key={flexField.label}
                            id="flex-field-text"
                            name={fieldName}
                            label={flexField.label}
                            value={fieldText}
                            onChange={handleFieldTextChange}
                            isInvalid={!!validationError}
                            isRequired={isRequired}
                        />
                        {isRequired && (
                            <RequiredValidation
                                value={fieldText}
                                displayInSummary
                                onValidated={(isInValid, error) =>
                                    onFormValidate(isInValid, error, dm[fieldName])
                                }
                                controlLabel={flexField.label}
                            />
                        )}
                    </>
                );
            }
            return (
                <>
                    <SmartSelector
                        isRequired={isRequired}
                        label={flexField.label}
                        className={fieldText}
                        options={flexField.options?.map((option) => ({
                            value: option,
                            label: option,
                        }))}
                        name={fieldName}
                        id="flex-field-select"
                        value={fieldText}
                        handleChange={handleFieldTextChange}
                        validationError={validationError}
                        includePlaceholderOption
                    />
                    {isRequired && (
                        <RequiredValidation
                            value={fieldText}
                            displayInSummary
                            onValidated={(isInValid, error) =>
                                onFormValidate(isInValid, error, dm[fieldName])
                            }
                            controlLabel={flexField.label}
                        />
                    )}
                </>
            );
        };

        const renderFieldsList = (flexField, index) => {
            return (
                <Stack className="per-section-group" key={flexField.type}>
                    <Heading headingLevel={2} appearance="h5">
                        {flexField.label}
                    </Heading>
                    <Text>{flexField.description}</Text>
                    <FlexRow>
                        <FlexCol xs={12} sm={8} md={6}>
                            {renderField(flexField, index)}
                        </FlexCol>
                    </FlexRow>
                </Stack>
            );
        };

        if (!permit.flex_fields?.length) return null;

        const stackSpace = permit.category === 'Vehicle Permit' ? 'xxl' : 'md';

        return (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <Stack space={stackSpace}>
                {permit.flex_fields
                    ?.filter(
                        ({ type }, key) =>
                            [TEXT_FIELD, DROPDOWN_FIELD].includes(type) && key <= 1
                    )
                    .map((flexField, index) => renderFieldsList(flexField, index))}
            </Stack>
        );
    }
    FlexField.propTypes = {
        permit: PropTypes.object,
        pActions: PropTypes.object,
        reservation: PropTypes.object,
    };

    return FlexField;
}
